// Updated Dashboard.tsx with straight lines and functional routing
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import { fetchDashboardMetrics } from "../store/slices/metricsSlice";
import { fetchPreferences } from "../store/slices/settingsSlice";
import { useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { summary, dailyMetrics, isLoading } = useSelector(
    (state: RootState) => state.metrics
  );
  const { preferences } = useSelector((state: RootState) => state.settings);
  const navigate = useNavigate();

  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("7d");

  useEffect(() => {
    dispatch(fetchDashboardMetrics({ period }));
    dispatch(fetchPreferences());
  }, [dispatch, period]);

  // Format currency based on user preferences
  const formatCurrency = (amount: number) => {
    const currency = preferences?.currency || "USD";
    const symbols: { [key: string]: string } = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "C$",
      AUD: "A$",
    };

    return `${symbols[currency] || "$"}${amount.toFixed(2)}`;
  };

  // Convert amount to user's currency
  const convertCurrency = (amount: number) => {
    const baseRate = 1; // USD is base
    const rates: { [key: string]: number } = {
      USD: 1.0,
      EUR: 0.92,
      GBP: 0.79,
      JPY: 148.0,
      CAD: 1.36,
      AUD: 1.52,
    };

    const targetCurrency = preferences?.currency || "USD";
    const targetRate = rates[targetCurrency] || 1;
    return amount * targetRate;
  };

  // Calculate last payment (assuming last payment was 30% of current balance)
  const calculateLastPayment = () => {
    const balance = summary?.balance || 0;
    return balance * 0.3;
  };

  // Calculate next payment (assuming next payment will be 70% of current balance)
  const calculateNextPayment = () => {
    const balance = summary?.balance || 0;
    return balance * 0.7;
  };

  // Estimated Earnings Section
  const earningsData = [
    {
      period: "Today so far",
      amount: convertCurrency(
        summary?.total_revenue ? summary.total_revenue * 0.1 : 0
      ),
      change: 12.5,
    },
    {
      period: "Yesterday",
      amount: convertCurrency(
        summary?.total_revenue ? summary.total_revenue * 0.15 : 0
      ),
      change: -2.3,
    },
    {
      period: "Last 7 days",
      amount: convertCurrency(summary?.total_revenue || 0),
      change: summary?.avg_ctr ? (summary.avg_ctr * 100).toFixed(1) : 0,
    },
    {
      period: "Last 28 days",
      amount: convertCurrency(
        summary?.total_revenue ? summary.total_revenue * 4 : 0
      ),
      change: 15.2,
    },
  ];

  // Function to generate comparison data for each metric with straight lines
  const generateComparisonData = (metricName: string) => {
    const days = period === "7d" ? 7 : period === "30d" ? 30 : 90;

    return Array.from({ length: days }, (_, i) => {
      const date = i + 1;

      // Current period data
      const currentValue = getMetricValue(metricName, "current", i, days);

      // Previous period data
      const previousValue = getMetricValue(metricName, "previous", i, days);

      return {
        date,
        current: currentValue,
        previous: previousValue,
      };
    });
  };

  const getMetricValue = (
    metricName: string,
    type: "current" | "previous",
    index: number,
    totalDays: number
  ) => {
    let baseValue: number;
    const isCurrent = type === "current";

    switch (metricName) {
      case "Page views":
        baseValue = summary?.total_page_views
          ? summary.total_page_views / totalDays
          : 1000;
        break;
      case "Impressions":
        baseValue = summary?.total_impressions
          ? summary.total_impressions / totalDays
          : 800;
        break;
      case "Clicks":
        baseValue = summary?.total_clicks
          ? summary.total_clicks / totalDays
          : 25;
        break;
      case "CPC":
        baseValue = summary?.avg_cpc || 1.5;
        break;
      case "CTR":
        baseValue = summary?.avg_ctr || 2.5;
        break;
      case "CPM":
        baseValue = summary?.avg_cpm || 4.0;
        break;
      default:
        baseValue = 100;
    }

    // Create straight line pattern with slight variation
    const trend = isCurrent ? 1 : 0.9;
    const step = index / totalDays;

    // Straight line with very slight variation for visual interest
    const variation = 0.95 + step * 0.1; // Very slight upward trend
    return baseValue * trend * variation;
  };

  // Performance Metrics with comparison data
  const performanceMetrics = [
    {
      name: "Page views",
      current: summary?.total_page_views || 0,
      previous: Math.round((summary?.total_page_views || 0) * 0.95),
      unit: "",
      chartData: generateComparisonData("Page views"),
    },
    {
      name: "Impressions",
      current: summary?.total_impressions || 0,
      previous: Math.round((summary?.total_impressions || 0) * 0.94),
      unit: "",
      chartData: generateComparisonData("Impressions"),
    },
    {
      name: "Clicks",
      current: summary?.total_clicks || 0,
      previous: Math.round((summary?.total_clicks || 0) * 0.89),
      unit: "",
      chartData: generateComparisonData("Clicks"),
    },
    {
      name: "CPC",
      current: summary?.avg_cpc || 0,
      previous: summary?.avg_cpc ? summary.avg_cpc * 1.11 : 0,
      unit: "$",
      chartData: generateComparisonData("CPC"),
    },
    {
      name: "CTR",
      current: summary?.avg_ctr || 0,
      previous: summary?.avg_ctr ? summary.avg_ctr * 0.958 : 0,
      unit: "%",
      chartData: generateComparisonData("CTR"),
    },
    {
      name: "CPM",
      current: summary?.avg_cpm || 0,
      previous: summary?.avg_cpm ? summary.avg_cpm * 0.928 : 0,
      unit: "$",
      chartData: generateComparisonData("CPM"),
    },
  ];

  // Calculate percentage change for each metric
  const getPercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  // Bid Types Data
  const bidTypesData = [
    { name: "CPM bids", value: 65, color: "#4285F4" },
    { name: "CPC bids", value: 25, color: "#34A853" },
    { name: "Active view CPM", value: 10, color: "#FBBC04" },
  ];

  // User balance from backend
  const userBalance = summary?.balance || 0;

  const handlePeriodChange = (newPeriod: "7d" | "30d" | "90d") => {
    setPeriod(newPeriod);
  };

  // Function to handle view report navigation
  const handleViewPerformanceReport = () => {
    navigate("/performance");
  };

  const handleViewBidTypeReport = () => {
    // You can add a bid type report route if needed, or use performance for now
    navigate("/performance");
  };

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-normal text-gray-900">
            Home
          </h1>
          <p className="text-sm text-gray-600">
            Overview of your AdSense performance
          </p>
        </div>
      </div>

      {/* Estimated Earnings Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white lg:col-span-3">
          <h2 className="text-lg font-medium mb-4">Estimated earnings</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {earningsData.map((item) => (
              <div key={item.period} className="flex flex-col">
                <p className="text-xs opacity-90">{item.period}</p>
                <span className="text-2xl font-normal">
                  {formatCurrency(item.amount)}
                </span>
                <div className="flex items-center gap-1 mt-1">
                  {typeof item.change === "number" && item.change >= 0 ? (
                    <ChevronUpIcon className="w-3 h-3" />
                  ) : (
                    <ChevronDownIcon className="w-3 h-3" />
                  )}
                  <span className="text-xs">
                    {typeof item.change === "number" && item.change >= 0
                      ? "+"
                      : ""}
                    {typeof item.change === "number"
                      ? item.change.toFixed(1)
                      : item.change}
                    %
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <h2 className="text-lg font-medium mb-4">Balance</h2>
          <div className="flex flex-col">
            <span className="text-2xl font-normal">
              {formatCurrency(convertCurrency(userBalance))}
            </span>
            <span className="text-xs opacity-90 mt-1">
              {preferences?.currency || "USD"}
            </span>
            <span className="text-xs opacity-90 mt-1">
              Available for payment
            </span>
          </div>
        </div>
      </div>

      {/* Performance and Bid Types Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Performance</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-600">Period:</span>
                <select
                  value={period}
                  onChange={(e) =>
                    handlePeriodChange(e.target.value as "7d" | "30d" | "90d")
                  }
                  className="px-3 py-1 text-sm border border-gray-300 rounded bg-white"
                >
                  <option value="7d">Last 7 days vs previous 7 days</option>
                  <option value="30d">Last 30 days vs previous 30 days</option>
                  <option value="90d">Last 90 days vs previous 90 days</option>
                </select>
              </div>
            </div>
            <button className="text-gray-400 hover:text-gray-600">
              <EllipsisVerticalIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Performance Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {performanceMetrics.map((metric) => {
              const change = getPercentageChange(
                metric.current,
                metric.previous
              );
              const isIncrease = change >= 0;

              return (
                <div key={metric.name} className="flex flex-col rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">{metric.name}</p>

                  <div className="mb-1">
                    <span className="text-lg font-normal">
                      {metric.unit}
                      {metric.name === "CTR"
                        ? metric.current.toFixed(2) + "%"
                        : metric.name === "Page views" ||
                          metric.name === "Impressions" ||
                          metric.name === "Clicks"
                        ? metric.current.toLocaleString()
                        : metric.current.toFixed(2)}
                    </span>
                  </div>

                  {/* Change percentage below value */}
                  <div className="flex items-center gap-1 mb-3">
                    {isIncrease ? (
                      <ChevronUpIcon className="w-3 h-3 text-green-600" />
                    ) : (
                      <ChevronDownIcon className="w-3 h-3 text-red-600" />
                    )}
                    <span
                      className={`text-xs ${
                        isIncrease ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {isIncrease ? "+" : ""}
                      {change.toFixed(1)}%
                    </span>
                  </div>

                  {/* Straight line chart - one thick, one thin line */}
                  <div className="h-16">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={metric.chartData}
                        margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
                      >
                        {/* Current period - thick solid line */}
                        <Line
                          type="linear" // Changed from "monotone" to "linear" for straight lines
                          dataKey="current"
                          stroke="#1d4ed8"
                          strokeWidth={2}
                          dot={false}
                          connectNulls={true}
                        />
                        {/* Previous period - thin dashed line */}
                        <Line
                          type="linear" // Changed from "monotone" to "linear" for straight lines
                          dataKey="previous"
                          stroke="#9ca3af"
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          dot={false}
                          connectNulls={true}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <button
              onClick={handleViewPerformanceReport}
              className="text-sm text-blue-600 hover:text-blue-700 uppercase font-medium"
            >
              View detailed report
            </button>
          </div>
        </div>

        {/* Bid Types Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 flex flex-col justify-between">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Bid types</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm text-gray-600">Period:</span>
                <select
                  value={period}
                  onChange={(e) =>
                    handlePeriodChange(e.target.value as "7d" | "30d" | "90d")
                  }
                  className="px-3 py-1 text-sm border border-gray-300 rounded bg-white"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                </select>
              </div>
            </div>
          </div>

          {/* Ring Chart */}
          <div className="h-48 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={bidTypesData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {bidTypesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Simple legend */}
          <div className="flex justify-center space-x-4 mt-4">
            {bidTypesData.map((item) => (
              <div key={item.name} className="flex items-center">
                <div
                  className="w-2 h-2 rounded-full mr-1"
                  style={{ backgroundColor: item.color }}
                />
                <span className="text-xs text-gray-600">{item.name}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-6 pt-4 border-t">
            <button
              onClick={handleViewBidTypeReport}
              className="text-sm text-blue-600 hover:text-blue-700 uppercase font-medium"
            >
              View bid type report
            </button>
          </div>
        </div>
      </div>

      {/* Currency Info */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <InformationCircleIcon className="h-6 w-6 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-gray-900 mb-2">
              Payment Information
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Your account is set to {preferences?.currency || "USD"}. All
              amounts are displayed in your selected currency.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="text-sm text-gray-600">Current balance</p>
                <p className="text-lg font-medium text-gray-900">
                  {formatCurrency(convertCurrency(userBalance))}
                </p>
              </div>
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="text-sm text-gray-600">Currency</p>
                <p className="text-lg font-medium text-gray-900">
                  {preferences?.currency || "USD"}
                </p>
              </div>
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="text-sm text-gray-600">Last payment</p>
                <p className="text-lg font-medium text-gray-900">
                  {formatCurrency(convertCurrency(calculateLastPayment()))}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  30% of current balance
                </p>
              </div>
              <div className="bg-white p-3 rounded border border-blue-100">
                <p className="text-sm text-gray-600">Next payment</p>
                <p className="text-lg font-medium text-gray-900">
                  {formatCurrency(convertCurrency(calculateNextPayment()))}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  70% of current balance
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
